import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { translateText } from '../i18n/translations';

const LanguageContext = createContext({
  lang: 'uz', // 'uz' | 'ru' | 'en'
  setLang: () => {},
  t: (text, fallback) => fallback || text
});

export const useLanguage = () => useContext(LanguageContext);

// Backwards compatibility alias for components still referencing useAlphabet
export const useAlphabet = () => {
  const { lang, setLang, t } = useContext(LanguageContext);
  return {
    alphabet: lang === 'uz' ? 'new' : lang,
    setAlphabet: (val) => {
      if (val === 'latin' || val === 'new') setLang('uz');
      else setLang(val);
    },
    t
  };
};

function translateWithWhitespace(rawText, lang) {
  if (!rawText || typeof rawText !== 'string') return rawText;
  const match = rawText.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match) return rawText;
  const leading = match[1];
  const core = match[2];
  const trailing = match[3];
  if (!core) return rawText;

  // Skip pure symbols, numbers, emails, URLs
  if (/^[\d\s.,:;+\-/%$€£*#!?()[\]{}•—–"'\\]+$/.test(core) || core.includes('@') || core.startsWith('http')) {
    return rawText;
  }

  const translated = translateText(core, lang);
  return leading + translated + trailing;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem('portal_lang') || 'uz';
    } catch {
      return 'uz';
    }
  });

  const setLang = (newLang) => {
    setLangState(newLang);
    try {
      localStorage.setItem('portal_lang', newLang);
      document.documentElement.setAttribute('lang', newLang);
    } catch (e) {
      console.warn('Could not save language preference', e);
    }
  };

  useEffect(() => {
    try {
      document.documentElement.setAttribute('lang', lang);
    } catch (e) {}
  }, [lang]);

  // Explicit translation helper for components
  const t = useCallback((textOrKey, fallback) => {
    if (!textOrKey) return fallback || '';
    return translateText(textOrKey, lang) || fallback || textOrKey;
  }, [lang]);

  // Global DOM Text Node & Input Placeholder Translation Walker
  const isTranslatingRef = useRef(false);

  useEffect(() => {
    const applyDomTranslations = () => {
      if (isTranslatingRef.current) return;
      isTranslatingRef.current = true;

      try {
        const root = document.getElementById('root') || document.body;

        // 1. Translate all text nodes
        const walker = document.createTreeWalker(
          root,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode(node) {
              const parent = node.parentElement;
              if (!parent) return NodeFilter.FILTER_REJECT;

              const tag = parent.tagName.toLowerCase();
              if (
                tag === 'script' ||
                tag === 'style' ||
                tag === 'input' ||
                tag === 'textarea' ||
                tag === 'code' ||
                tag === 'pre' ||
                tag === 'svg' ||
                tag === 'path' ||
                parent.isContentEditable ||
                parent.classList.contains('no-translit') ||
                parent.classList.contains('site-direct-link') ||
                parent.closest('input') ||
                parent.closest('textarea') ||
                parent.closest('.no-translit')
              ) {
                return NodeFilter.FILTER_REJECT;
              }

              if (!node.nodeValue || !node.nodeValue.trim()) {
                return NodeFilter.FILTER_REJECT;
              }

              const val = node.nodeValue.trim();
              if (/^[\d\s.,:;+\-/%$€£*#!?()[\]{}]+$/.test(val) || val.includes('@') || val.startsWith('http')) {
                return NodeFilter.FILTER_REJECT;
              }

              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );

        let currentNode;
        while ((currentNode = walker.nextNode())) {
          // If the text node was modified by React or external code, refresh __origText
          if (currentNode.__origText === undefined || currentNode.nodeValue !== currentNode.__lastTarget) {
            currentNode.__origText = currentNode.nodeValue;
          }

          const target = translateWithWhitespace(currentNode.__origText, lang);
          if (currentNode.nodeValue !== target) {
            currentNode.nodeValue = target;
          }
          currentNode.__lastTarget = target;
        }

        // 2. Translate placeholders of inputs and textareas
        const inputs = root.querySelectorAll('input[placeholder], textarea[placeholder]');
        inputs.forEach((el) => {
          if (el.classList.contains('no-translit')) return;
          if (el.__origPlaceholder === undefined) {
            el.__origPlaceholder = el.getAttribute('placeholder') || '';
          }
          if (el.__origPlaceholder) {
            const targetPh = translateText(el.__origPlaceholder, lang);
            if (el.getAttribute('placeholder') !== targetPh) {
              el.setAttribute('placeholder', targetPh);
            }
          }
        });
      } finally {
        isTranslatingRef.current = false;
      }
    };

    // Initial translation run
    applyDomTranslations();

    // Observe dynamic DOM changes (AJAX, user interaction, modals, React state changes)
    const observer = new MutationObserver((mutations) => {
      if (isTranslatingRef.current) return;
      let hasSignificantChange = false;
      for (const m of mutations) {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          hasSignificantChange = true;
          break;
        }
        if (m.type === 'characterData') {
          if (m.target && m.target.__lastTarget !== m.target.nodeValue) {
            hasSignificantChange = true;
            break;
          }
        }
      }
      if (hasSignificantChange) {
        window.requestAnimationFrame(applyDomTranslations);
      }
    });

    const root = document.getElementById('root') || document.body;
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => {
      observer.disconnect();
    };
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageContext;
